<script setup>
import Icon from '@components/Icon.vue'
import Spinner from '@components/Spinner.vue'
import BaseModal from '@components/BaseModal.vue'
import axios from 'axios'
import { ref, reactive, computed } from 'vue'
import { $isSignupOpen } from '../store'
import { useStore } from '@nanostores/vue'

const isSignupOpen = computed({
  get() {
    return useStore($isSignupOpen).value
  },
  set(val) {
    $isSignupOpen.set(val)
  },
})

const failed = ref(false)
const submitted = ref(false)
const submitting = ref(false)
const form = reactive({
  company: '',
  address_1: '',
  city: '',
  state: '',
  zipcode: '',
  name: '',
  phone: '',
  email: '',
  email_confirmation: '',
  password: '',
  referral: '',
})

function register () {
  submitting.value = true
  axios.post('https://crm.hometownheroerp.com/leads/submit', form)
      .then(response => {
        submitted.value = true
      })
      .catch(() => {
        failed.value = true
      })
      .finally(() => {
        submitting.value = false
      })
}
</script>

<template>
  <BaseModal v-model="isSignupOpen" title="Wholesale Application">
    <div class="alert alert--error" v-if="failed">
      <Icon type="error" />
      <span>There was a problem with your registration. Please contact us at (512) 387-1613.</span>
    </div>

    <div class="alert alert--success" v-if="submitted">
      <Icon type="success" />
      <span>Thank you for submitting! We will reach out to you within 24 hours.</span>
    </div>

    <form @submit.prevent="register" v-if="!submitted" class="registration">
      <div>
        <label class="sr-only" for="Company">Business Name</label>
        <input type="text" v-model="form.company" id="Company" class="input" placeholder="Business Name" required>
      </div>
      <div>
        <label class="sr-only" for="Address1">Business Address</label>
        <input type="text" v-model="form.address_1" id="Address1" class="input" placeholder="Business Address" required>
      </div>
      <div class="fields">
        <div>
          <label class="sr-only" for="City">City</label>
          <input type="text" v-model="form.city" id="City" class="input" placeholder="City" required>
        </div>
        <div>
          <label class="sr-only" for="State">City</label>
          <select v-model="form.state" id="State" class="select" required>
            <option value="" selected="" disabled="">State</option>
            <option value="AL">Alabama</option>
            <option value="AK">Alaska</option>
            <option value="AZ">Arizona</option>
            <option value="AR">Arkansas</option>
            <option value="CA">California</option>
            <option value="CO">Colorado</option>
            <option value="CT">Connecticut</option>
            <option value="DE">Delaware</option>
            <option value="DC">District Of Columbia</option>
            <option value="FL">Florida</option>
            <option value="GA">Georgia</option>
            <option value="HI">Hawaii</option>
            <option value="ID">Idaho</option>
            <option value="IL">Illinois</option>
            <option value="IN">Indiana</option>
            <option value="IA">Iowa</option>
            <option value="KS">Kansas</option>
            <option value="KY">Kentucky</option>
            <option value="LA">Louisiana</option>
            <option value="ME">Maine</option>
            <option value="MD">Maryland</option>
            <option value="MA">Massachusetts</option>
            <option value="MI">Michigan</option>
            <option value="MN">Minnesota</option>
            <option value="MS">Mississippi</option>
            <option value="MO">Missouri</option>
            <option value="MT">Montana</option>
            <option value="NE">Nebraska</option>
            <option value="NV">Nevada</option>
            <option value="NH">New Hampshire</option>
            <option value="NJ">New Jersey</option>
            <option value="NM">New Mexico</option>
            <option value="NY">New York</option>
            <option value="NC">North Carolina</option>
            <option value="ND">North Dakota</option>
            <option value="OH">Ohio</option>
            <option value="OK">Oklahoma</option>
            <option value="OR">Oregon</option>
            <option value="PA">Pennsylvania</option>
            <option value="RI">Rhode Island</option>
            <option value="SC">South Carolina</option>
            <option value="SD">South Dakota</option>
            <option value="TN">Tennessee</option>
            <option value="TX">Texas</option>
            <option value="UT">Utah</option>
            <option value="VT">Vermont</option>
            <option value="VA">Virginia</option>
            <option value="WA">Washington</option>
            <option value="WV">West Virginia</option>
            <option value="WI">Wisconsin</option>
            <option value="WY">Wyoming</option>
          </select>
        </div>
        <div>
          <label class="sr-only" for="Zipcode">Zipcode</label>
          <input type="text" v-model="form.zipcode" id="Zipcode" class="input" placeholder="Zipcode" required>
        </div>
      </div>
      <div>
        <label class="sr-only" for="Name">Personal Name</label>
        <input type="text" v-model="form.name" id="Name" class="input" placeholder="Personal Name" required>
      </div>
      <div class="fields">
        <div>
          <label class="sr-only" for="Phone">Phone</label>
          <input type="tel" v-model="form.phone" id="Phone" class="input" placeholder="Phone" required>
        </div>
        <div>
          <label class="sr-only" for="Website">Website</label>
          <input type="text" v-model="form.website" id="Website" class="input" placeholder="Website">
        </div>
      </div>
      <div class="fields">
        <div>
          <label class="sr-only" for="Email">Email</label>
          <input type="email" v-model="form.email" id="Email" class="input" placeholder="Email" required>
        </div>
        <div>
          <label class="sr-only" for="ConfirmEmail">Confirm Email</label>
          <input type="email" v-model="form.email_confirmation" id="ConfirmEmail" class="input" placeholder="Confirm Email" required>
        </div>
      </div>
      <div class="fields">
        <div class="field">
          <label class="sr-only" for="Password">Password</label>
          <input type="password" v-model="form.password" id="Password" class="input" placeholder="Password" required>
        </div>
        <div>
          <select v-model="form.referral" id="Referral" class="select" required>
            <option value="" selected="" disabled="">How did you hear about us?</option>
            <option>Texas $2,000 Free Mailer</option>
            <option>AATAC</option>
            <option>DSHS D8 Updates</option>
            <option>Social Media</option>
            <option>Referral from Customer</option>
            <option>Referral from Friend</option>
            <option>Google Search</option>
            <option>Phone Call From Rep</option>
            <option>Online Distributor Website</option>
            <option>Existing Customer</option>
            <option>Other</option>
          </select>
        </div>
      </div>
      <div class="registration__actions">
        <button type="submit" class="button">
          <Spinner color="white" v-if="submitting" />
          <span v-else>Get Started</span>
        </button>
        <a href="https://wholesale.hometownhero.com/account/login">Already Have An Account? Login Here</a>
      </div>
    </form>
  </BaseModal>
</template>

<style lang="scss" scoped>
.registration {
  .input, .select {
    display: block;
    width: 100%;
    margin-bottom: 8px;

    @media screen and (min-width: 768px) {
      margin-bottom: 16px;
    }
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;

    @media screen and (min-width: 768px) {
      gap: 16px;
      margin-bottom: 16px;
    }

    .input, .select {
      margin-bottom: 0;
    }

    @media screen and (min-width: 768px) {
      flex-direction: row;
    }

    & > * {
      flex: 1;
    }
  }

  .registration__actions {
    text-align: center;

    .button {
      margin: 16px auto;
      display: block;
      border-radius: 24px;
      padding: 14px;
    }
  }
}
</style>
